import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  FileText, Route as RouteIcon, BookMarked, MessageSquare, Users as UsersIcon,
  MessageCircle, Trash2, TrendingUp, Sparkles, Command, Plus, ExternalLink, Clock,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Admin dashboard landing page. Loads on `/admin` (no `?tab=` param) instead
 * of the old blog-upload default. Surfaces:
 *   • Greeting + primary CTAs (Upload blog, Add pathway, Open palette hint)
 *   • Stats grid (blogs, pathways, glossary, comments to review, feedback,
 *     total reads, trash size) — clickable to jump to the matching tab.
 *   • Recent activity: last 5 blogs, last 5 comments/feedback items.
 *
 * Everything is read-only + non-destructive here. Anything the admin actually
 * needs to *do* is one click away in the top nav or via Cmd/Ctrl+K.
 */
export default function AdminDashboardPanel({
  blogs = [], pathways = [], glossary = [],
  commentCounts = { unreviewed: 0, total: 0 },
  feedbackCounts = { unreviewed: 0, total: 0 },
  onNavigate,          // (tabValue) => void   — parent's pickTab
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recentComments, setRecentComments] = useState([]);
  const [recentFeedback, setRecentFeedback] = useState([]);
  const [trashCount, setTrashCount] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [usersTotal, setUsersTotal] = useState(null);

  useEffect(() => {
    api.get("/admin/comments?limit=5")
      .then(r => setRecentComments((r.data || []).slice(0, 5)))
      .catch(() => {});
    api.get("/admin/feedback?limit=5")
      .then(r => setRecentFeedback((r.data || []).slice(0, 5)))
      .catch(() => {});
    api.get("/admin/blogs/trash")
      .then(r => setTrashCount((r.data || []).length))
      .catch(() => {});
    // /analytics/overview is admin-only; response includes { total_views,
    // unique_users_period, unique_sessions, ... }. We reuse that for the
    // "total reads" + "registered users" stat cells so the dashboard
    // matches what the Analytics tab shows.
    api.get("/analytics/overview?days=90")
      .then(r => setAnalytics(r.data))
      .catch(() => {});
    api.get("/users")
      .then(r => setUsersTotal((r.data || []).length))
      .catch(() => {});
  }, []);

  const draftCount = blogs.filter(b =>
    !b.release_date || b.release_date > new Date().toISOString().slice(0, 10)
  ).length;
  const publishedCount = blogs.length - draftCount;
  const hiddenPathways = pathways.filter(p => p.visible === false).length;
  const recentBlogs = [...blogs]
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
    .slice(0, 5);
  const firstName = (user?.name || "").split(" ")[0] || "there";

  const stats = [
    {
      key: "blogs", label: "Blogs", value: blogs.length,
      hint: publishedCount === 0 && draftCount === 0
        ? "None yet — upload your first"
        : `${publishedCount} published · ${draftCount} draft/scheduled`,
      Icon: FileText, tab: "upload",
    },
    {
      key: "pathways", label: "Pathways", value: pathways.length,
      hint: hiddenPathways > 0
        ? `${hiddenPathways} hidden`
        : "All visible",
      Icon: RouteIcon, tab: "pathways",
    },
    {
      key: "glossary", label: "Glossary terms", value: glossary.length,
      hint: "Auto-links + hover popovers",
      Icon: BookMarked, tab: "glossary",
    },
    {
      key: "comments", label: "Comments", value: commentCounts.unreviewed,
      hint: commentCounts.unreviewed > 0
        ? `${commentCounts.unreviewed} to review`
        : `${commentCounts.total ?? 0} total · all reviewed`,
      Icon: MessageCircle, tab: "comments",
      urgent: commentCounts.unreviewed > 0,
    },
    {
      key: "feedback", label: "Feedback", value: feedbackCounts.unreviewed,
      hint: feedbackCounts.unreviewed > 0
        ? `${feedbackCounts.unreviewed} to review`
        : `${feedbackCounts.total ?? 0} total · all reviewed`,
      Icon: MessageSquare, tab: "feedback",
      urgent: feedbackCounts.unreviewed > 0,
    },
    {
      key: "reads", label: "Reads (90d)",
      value: analytics?.total_views ?? "—",
      hint: analytics?.unique_sessions
        ? `${analytics.unique_sessions} unique sessions`
        : "Blog reads in the last 90 days",
      Icon: TrendingUp, tab: "analytics",
    },
    {
      key: "users", label: "Registered users",
      value: usersTotal ?? "—",
      hint: "Signed-in accounts",
      Icon: UsersIcon, tab: "users",
    },
    {
      key: "trash", label: "Trash", value: trashCount,
      hint: trashCount > 0 ? "Soft-deleted blogs" : "Empty",
      Icon: Trash2, tab: "trash",
    },
  ];

  return (
    <div data-testid="admin-dashboard-panel" className="space-y-8 pt-6">
      {/* Greeting */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-3xl font-semibold text-slate-900">
            Welcome back, {firstName}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Here's what's going on across the platform right now.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid="dash-cta-upload"
            onClick={() => onNavigate?.("upload")}
            className="bg-sky-700 hover:bg-sky-600 gap-2"
          >
            <Plus className="w-4 h-4"/>Upload blog
          </Button>
          <Button
            data-testid="dash-cta-pathways"
            variant="outline"
            onClick={() => onNavigate?.("pathways")}
            className="gap-2"
          >
            <RouteIcon className="w-4 h-4"/>Pathways
          </Button>
          <div
            data-testid="dash-cta-palette-hint"
            title="Open the command palette to jump anywhere"
            className="inline-flex items-center gap-2 text-xs text-slate-500 px-3 py-2 border border-slate-200 rounded-md bg-slate-50"
          >
            <Command className="w-3.5 h-3.5"/>
            <span>Press</span>
            <kbd className="text-[10px] uppercase tracking-widest bg-white border border-slate-200 px-1.5 py-0.5 rounded">Cmd/Ctrl</kbd>
            <span>+</span>
            <kbd className="text-[10px] uppercase tracking-widest bg-white border border-slate-200 px-1.5 py-0.5 rounded">K</kbd>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map(s => {
          const Icon = s.Icon;
          return (
            <button
              key={s.key}
              data-testid={`dash-stat-${s.key}`}
              onClick={() => onNavigate?.(s.tab)}
              className={`group text-left rounded-lg border p-4 transition-colors bg-white hover:border-sky-400 hover:shadow-sm ${
                s.urgent ? "border-amber-300 bg-amber-50/50" : "border-slate-200"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  {s.label}
                </span>
                <Icon className={`w-4 h-4 ${s.urgent ? "text-amber-700" : "text-slate-400 group-hover:text-sky-700"}`}/>
              </div>
              <div className={`mt-2 font-heading text-3xl font-semibold ${s.urgent ? "text-amber-900" : "text-slate-900"}`}>
                {s.value}
              </div>
              <div className={`mt-1 text-xs ${s.urgent ? "text-amber-800" : "text-slate-500"} truncate`}>
                {s.hint}
              </div>
            </button>
          );
        })}
      </div>

      {/* Two-column activity feed */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent blogs */}
        <section
          data-testid="dash-recent-blogs"
          className="border border-slate-200 rounded-lg bg-white overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/50">
            <div className="inline-flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-500"/>
              <h3 className="font-heading text-sm font-semibold text-slate-900">Recent blogs</h3>
            </div>
            <button
              onClick={() => onNavigate?.("upload")}
              className="text-xs text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"
            >
              View all<ExternalLink className="w-3 h-3"/>
            </button>
          </div>
          <ul className="divide-y divide-slate-100">
            {recentBlogs.length === 0 && (
              <li className="px-4 py-6 text-sm text-slate-400 italic">
                No blogs yet — upload your first .docx to get started.
              </li>
            )}
            {recentBlogs.map(b => {
              const isDraft = !b.release_date || b.release_date > new Date().toISOString().slice(0, 10);
              return (
                <li key={b.blog_id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-slate-400 shrink-0">{b.blog_id}</span>
                      {isDraft && (
                        <span className="text-[9px] uppercase tracking-widest bg-amber-100 text-amber-800 rounded px-1 py-0.5">
                          {b.release_date ? "Scheduled" : "Draft"}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-sm text-slate-900 truncate font-medium">
                      {b.title || "Untitled"}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3"/>
                      {b.created_at ? new Date(b.created_at).toLocaleDateString(undefined, {
                        year: "numeric", month: "short", day: "numeric",
                      }) : "—"}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/blogs/${b.slug}`)}
                    title="Open in the reader"
                    className="text-xs text-slate-400 hover:text-sky-700 shrink-0"
                  >
                    <ExternalLink className="w-3.5 h-3.5"/>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        {/* Recent comments + feedback stacked */}
        <div className="space-y-6">
          <ActivityList
            testid="dash-recent-comments"
            title="Recent comments"
            Icon={MessageCircle}
            emptyText="No comments yet."
            items={recentComments.map(c => ({
              id: c.comment_id,
              primary: c.body?.slice(0, 90) || "(empty)",
              secondary: `on "${c.blog_title || 'a blog'}"`,
              time: c.created_at,
              pending: c.approved === false,
              onOpen: () => c.blog_slug && navigate(`/blogs/${c.blog_slug}`),
            }))}
            onViewAll={() => onNavigate?.("comments")}
          />
          <ActivityList
            testid="dash-recent-feedback"
            title="Recent feedback"
            Icon={Sparkles}
            emptyText="No feedback yet."
            items={recentFeedback.map(f => ({
              id: f.feedback_id || f.id,
              primary: (f.message || f.body || "(empty)").slice(0, 90),
              secondary: `${f.type ? f.type : "Feedback"} · ${f.user_email || "anonymous"}`,
              time: f.created_at,
              pending: f.reviewed === false,
              onOpen: () => onNavigate?.("feedback"),
            }))}
            onViewAll={() => onNavigate?.("feedback")}
          />
        </div>
      </div>
    </div>
  );
}

/** Small reusable activity list — shared between comments + feedback. */
function ActivityList({ testid, title, Icon, items = [], emptyText, onViewAll }) {
  return (
    <section
      data-testid={testid}
      className="border border-slate-200 rounded-lg bg-white overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/50">
        <div className="inline-flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-500"/>
          <h3 className="font-heading text-sm font-semibold text-slate-900">{title}</h3>
        </div>
        <button
          onClick={onViewAll}
          className="text-xs text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"
        >
          View all<ExternalLink className="w-3 h-3"/>
        </button>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.length === 0 && (
          <li className="px-4 py-5 text-sm text-slate-400 italic">{emptyText}</li>
        )}
        {items.map(it => (
          <li key={it.id} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-900 leading-snug line-clamp-2">
                {it.primary}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500 truncate">
                {it.secondary}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-400 inline-flex items-center gap-1">
                <Clock className="w-3 h-3"/>
                {it.time ? new Date(it.time).toLocaleDateString(undefined, {
                  year: "numeric", month: "short", day: "numeric",
                }) : "—"}
                {it.pending && (
                  <span className="ml-2 text-[9px] uppercase tracking-widest bg-amber-100 text-amber-800 rounded px-1 py-0.5">
                    Pending
                  </span>
                )}
              </div>
            </div>
            {it.onOpen && (
              <button
                onClick={it.onOpen}
                className="text-xs text-slate-400 hover:text-sky-700 shrink-0"
                title="Open"
              >
                <ExternalLink className="w-3.5 h-3.5"/>
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
